/**
 * Email validation utilities
 * Provides comprehensive email validation with proper domain and TLD checking
 */

/**
 * Validates if a TLD (Top Level Domain) is valid
 * @param {string} tld - The TLD to validate
 * @returns {boolean} - True if the TLD is valid
 */
const isValidTld = tld =>
    // TLD must be at least 2 characters and contain only letters
    tld.length >= 2 && /^[a-zA-Z]+$/.test(tld);

/**
 * Validates if a domain label is valid
 * @param {string} label - The domain label to validate
 * @returns {boolean} - True if the label is valid
 */
const isValidDomainLabel = label => {
    // Label must be non-empty
    if (!label || label.trim() === "") return false;

    // Label must start and end with alphanumeric character
    const firstChar = label[0];
    const lastChar = label[label.length - 1];

    return /^[a-zA-Z0-9]/.test(firstChar) &&
           /[a-zA-Z0-9]$/.test(lastChar) &&
           /^[a-zA-Z0-9-]+$/.test(label) &&
           !label.includes("--");
};

/**
 * Validates if the domain part of an email is valid
 * @param {string} domainPart - The domain part to validate
 * @returns {boolean} - True if the domain part is valid
 */
const isValidDomainPart = domainPart => {
    // Domain must be non-empty and contain at least one dot
    if (!domainPart || domainPart.trim() === "" || !domainPart.includes(".")) return false;

    const domainLabels = domainPart.split(".");

    // All labels must be valid and TLD must meet specific requirements
    return domainLabels.every(isValidDomainLabel) &&
           isValidTld(domainLabels[domainLabels.length - 1]);
};

/**
 * Validates if the local part of an email is valid
 * @param {string} localPart - The local part to validate
 * @returns {boolean} - True if the local part is valid
 */
const isValidLocalPart = localPart =>
    // Local part must be non-empty
    localPart && localPart.trim() !== "";

/**
 * Validates if an email address is valid using strict validation rules
 * @param {string} email - The email address to validate
 * @returns {boolean} - True if the email is valid
 */
const isValidEmailStrict = email => {
    if (!email || email.trim() === "") return false;

    try {
        // Reject emails with non-ASCII characters
        if (!/^[ -~]*$/.test(email)) return false;

        // Split and validate email structure
        const parts = email.split("@");
        if (parts.length !== 2) return false;

        const [localPart, domainPart] = parts;

        return isValidLocalPart(localPart) &&
               isValidDomainPart(domainPart) &&
               /^[^@\s]+@[^@\s]+\.[^@\s]+$/i.test(email);
    } catch (error) {
        return false;
    }
};

export default {
    isValidEmailStrict
};
